import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Client } from '../types';
import { getClientProfile, loginClient as apiLoginClient, onClientUnauthorized } from '../api/portal';
import { clientStorage } from '../utils/clientStorage';

interface ClientAuthContextType {
  client: Client | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  refreshProfile: () => Promise<void>;
}

const ClientAuthContext = createContext<ClientAuthContextType | undefined>(undefined);

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext);
  if (!context) {
    throw new Error('useClientAuth must be used within a ClientAuthProvider');
  }
  return context;
};

interface ClientAuthProviderProps {
  children: React.ReactNode;
}

export const ClientAuthProvider: React.FC<ClientAuthProviderProps> = ({ children }) => {
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Load client profile on mount if token exists
  useEffect(() => {
    const token = clientStorage.getAccessToken();
    if (token) {
      loadClientProfile();
    } else {
      setLoading(false);
    }
  }, []);

  // Listen for unauthorized events from axios interceptor
  useEffect(() => {
    const unsubscribe = onClientUnauthorized(() => {
      // Clear client state and redirect to login
      setClient(null);
      navigate('/portal/login');
    });

    return unsubscribe;
  }, [navigate]);

  const loadClientProfile = async () => {
    try {
      const profile = await getClientProfile();
      setClient(profile);
    } catch (error) {
      console.error('Failed to load client profile:', error);
      clientStorage.clearTokens();
      setClient(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    const response = await apiLoginClient(email, password);

    // Store tokens
    clientStorage.setTokens(response.accessToken, response.refreshToken);

    // Set client
    setClient(response.client);
  };

  const logout = () => {
    clientStorage.clearTokens();
    setClient(null);
    navigate('/portal/login');
  };

  const refreshProfile = async () => {
    await loadClientProfile();
  };

  const value: ClientAuthContextType = {
    client,
    login,
    logout,
    isAuthenticated: !!client,
    loading,
    refreshProfile,
  };

  return (
    <ClientAuthContext.Provider value={value}>
      {children}
    </ClientAuthContext.Provider>
  );
};

export default ClientAuthContext;
